import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getQANotesForUser, markQANoteRead } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Search,
  Filter,
  User,
  Phone,
  Calendar,
  Clock,
  Eye,
  CheckCircle,
  AlertCircle,
  ThumbsUp,
  TrendingUp,
  MessageSquare,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Shield,
  Mail,
  MailOpen,
  Bell,
  Star,
  Inbox
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';

const NOTE_TYPE_CONFIG = {
  general: { label: 'General', color: 'bg-muted text-foreground dark:bg-slate-800 dark:text-muted-foreground/70', icon: MessageSquare },
  feedback: { label: 'Feedback', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: MessageSquare },
  action_required: { label: 'Action Required', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', icon: AlertCircle },
  positive: { label: 'Positive', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: ThumbsUp },
  improvement: { label: 'Needs Improvement', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', icon: TrendingUp }
};

export default function UserQANotes() {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [notes, setNotes] = useState([]);
  const [allNotes, setAllNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    read: 0,
    actionRequired: 0,
    positive: 0,
    improvement: 0
  });
  
  // Selected note dialog
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchNotes();
    }
  }, [user?.id]);

  useEffect(() => {
    filterNotes();
  }, [allNotes, activeTab, searchQuery, filterType, filterPeriod]);

  const fetchNotes = async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const { data } = await getQANotesForUser(user.id);
      const notesList = data.notes || data || [];
      setAllNotes(notesList);
      calculateStats(notesList);
    } catch (error) {
      console.error('Failed to fetch QA notes:', error);
      toast.error('Failed to load QA notes');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = (notesList) => {
    const total = notesList.length;
    const unread = notesList.filter(n => !n.is_read_by_user).length;
    const read = notesList.filter(n => n.is_read_by_user).length;
    const actionRequired = notesList.filter(n => n.note_type === 'action_required').length;
    const positive = notesList.filter(n => n.note_type === 'positive').length;
    const improvement = notesList.filter(n => n.note_type === 'improvement').length;
    
    setStats({ total, unread, read, actionRequired, positive, improvement });
  };

  const filterNotes = () => {
    let filtered = [...allNotes];
    
    // Filter by tab (read/unread)
    if (activeTab === 'unread') {
      filtered = filtered.filter(n => !n.is_read_by_user);
    } else if (activeTab === 'read') {
      filtered = filtered.filter(n => n.is_read_by_user);
    }
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(n => n.note_type === filterType);
    }
    
    // Filter by period
    if (filterPeriod !== 'all') {
      const now = new Date();
      let startDate;
      switch (filterPeriod) {
        case 'today':
          startDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        default:
          startDate = null;
      }
      if (startDate) {
        filtered = filtered.filter(n => new Date(n.created_at) >= startDate);
      }
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(n => 
        n.note_content?.toLowerCase().includes(query) ||
        n.auditor_name?.toLowerCase().includes(query) ||
        n.call_filename?.toLowerCase().includes(query) ||
        n.note_type?.toLowerCase().includes(query)
      );
    }
    
    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    setNotes(filtered);
  };

  const handleViewNote = async (note) => {
    setSelectedNote(note);
    setNoteDialogOpen(true);
    
    // Mark as read if not already
    if (!note.is_read_by_user) {
      try {
        await markQANoteRead(note.id);
        // Update local state
        setAllNotes(prev => prev.map(n => 
          n.id === note.id ? { ...n, is_read_by_user: true } : n
        ));
        calculateStats(allNotes.map(n => 
          n.id === note.id ? { ...n, is_read_by_user: true } : n
        ));
      } catch (error) {
        console.error('Failed to mark note as read:', error);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return format(date, 'MMM d');
    } catch {
      return '';
    }
  };

  const NoteCard = ({ note }) => {
    const typeConfig = NOTE_TYPE_CONFIG[note.note_type] || NOTE_TYPE_CONFIG.general;
    const TypeIcon = typeConfig.icon;
    const isUnread = !note.is_read_by_user;
    
    return (
      <Card 
        className={cn(
          "cursor-pointer transition-all hover:shadow-lg",
          isUnread && "border-l-4 border-l-blue-500",
          isDark ? "bg-slate-800/50 hover:bg-slate-800" : "bg-card hover:bg-muted/50"
        )}
        onClick={() => handleViewNote(note)}
        data-testid={`note-card-${note.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className={cn(
              "p-2 rounded-lg flex-shrink-0",
              typeConfig.color
            )}>
              <TypeIcon className="h-4 w-4" />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={typeConfig.color}>
                    {typeConfig.label}
                  </Badge>
                  {isUnread && (
                    <Badge className="bg-blue-500 text-white text-xs">New</Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {getRelativeTime(note.created_at)}
                </span>
              </div>
              
              <p className="mt-2 text-sm line-clamp-2 text-foreground">
                {note.note_content}
              </p>
              
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {note.auditor_name || 'QA Auditor'}
                </span>
                {note.call_filename && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {note.call_filename}
                  </span>
                )}
              </div>
            </div>
            
            {/* Action indicator */}
            <div className="flex-shrink-0">
              {isUnread ? (
                <Mail className="h-4 w-4 text-blue-500" />
              ) : (
                <MailOpen className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const StatCard = ({ icon: Icon, label, value, color, onClick, active }) => (
    <Card 
      className={cn(
        "cursor-pointer transition-all hover:shadow-md",
        active && "ring-2 ring-blue-500",
        isDark ? "bg-slate-800/50" : "bg-card"
      )}
      onClick={onClick}
      data-testid={`stat-card-${label.toLowerCase().replace(' ', '-')}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg", color)}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-qa-notes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My QA Notes</h1>
          <p className="text-muted-foreground">
            View feedback and notes from your quality auditors
          </p>
        </div>
        <Button onClick={fetchNotes} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard 
          icon={Inbox} 
          label="Total Notes" 
          value={stats.total}
          color="bg-muted text-foreground dark:bg-slate-800 dark:text-muted-foreground/70"
          onClick={() => setActiveTab('all')}
          active={activeTab === 'all'}
        />
        <StatCard 
          icon={Mail} 
          label="Unread" 
          value={stats.unread}
          color="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
          onClick={() => setActiveTab('unread')}
          active={activeTab === 'unread'}
        />
        <StatCard 
          icon={MailOpen} 
          label="Read" 
          value={stats.read}
          color="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
          onClick={() => setActiveTab('read')}
          active={activeTab === 'read'}
        />
        <StatCard 
          icon={AlertCircle} 
          label="Action Required" 
          value={stats.actionRequired}
          color="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
          onClick={() => { setActiveTab('all'); setFilterType('action_required'); }}
          active={filterType === 'action_required'}
        />
        <StatCard 
          icon={ThumbsUp} 
          label="Positive" 
          value={stats.positive}
          color="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
          onClick={() => { setActiveTab('all'); setFilterType('positive'); }}
          active={filterType === 'positive'}
        />
        <StatCard 
          icon={TrendingUp} 
          label="Improvement" 
          value={stats.improvement}
          color="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
          onClick={() => { setActiveTab('all'); setFilterType('improvement'); }}
          active={filterType === 'improvement'}
        />
      </div>

      {/* Filters and Search */}
      <Card className={isDark ? "bg-slate-800/50" : ""}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search notes by content, auditor, or call..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="qa-notes-search"
              />
            </div>
            
            {/* Type Filter */}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]" data-testid="type-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Note Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="feedback">Feedback</SelectItem>
                <SelectItem value="action_required">Action Required</SelectItem>
                <SelectItem value="positive">Positive</SelectItem>
                <SelectItem value="improvement">Improvement</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Period Filter */}
            <Select value={filterPeriod} onValueChange={setFilterPeriod}>
              <SelectTrigger className="w-[150px]" data-testid="period-filter">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Clear Filters */}
            {(searchQuery || filterType !== 'all' || filterPeriod !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('all');
                  setFilterPeriod('all');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" className="gap-2">
            <Inbox className="h-4 w-4" />
            All Notes ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="unread" className="gap-2">
            <Mail className="h-4 w-4" />
            Unread ({stats.unread})
          </TabsTrigger>
          <TabsTrigger value="read" className="gap-2">
            <MailOpen className="h-4 w-4" />
            Read ({stats.read})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {notes.length === 0 ? (
            <Card className={cn("p-12 text-center", isDark ? "bg-slate-800/50" : "")}>
              <Inbox className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Notes Found</h3>
              <p className="text-muted-foreground">
                {activeTab === 'unread' 
                  ? "You've read all your notes! Great job staying on top of feedback."
                  : activeTab === 'read'
                  ? "No read notes yet."
                  : searchQuery || filterType !== 'all' || filterPeriod !== 'all'
                  ? "No notes match your filters. Try adjusting your search criteria."
                  : "You don't have any QA notes yet."}
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {notes.map(note => (
                <NoteCard key={note.id} note={note} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Note Detail Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {selectedNote && (
                <Badge className={NOTE_TYPE_CONFIG[selectedNote.note_type]?.color || ''}>
                  {NOTE_TYPE_CONFIG[selectedNote.note_type]?.label || 'General'}
                </Badge>
              )}
              <DialogTitle>QA Note Details</DialogTitle>
            </div>
            <DialogDescription>
              {selectedNote && formatDate(selectedNote.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedNote && (
            <div className="space-y-6">
              {/* Auditor Info */}
              <div className="flex items-center gap-3 p-4 rounded-lg bg-muted dark:bg-slate-800">
                <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{selectedNote.auditor_name || 'QA Auditor'}</p>
                  <p className="text-sm text-muted-foreground">Quality Auditor</p>
                </div>
              </div>
              
              {/* Call Info */}
              {selectedNote.call_id && (
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Related Call</p>
                      <p className="text-xs text-muted-foreground">{selectedNote.call_filename || 'Call Recording'}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/user/${user?.unique_url}/calls?call=${selectedNote.call_id}`)}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Call
                  </Button>
                </div>
              )}
              
              {/* Note Content */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Note Content</h4>
                <div className="p-4 rounded-lg bg-muted/50 dark:bg-slate-900 whitespace-pre-wrap text-foreground">
                  {selectedNote.note_content}
                </div>
              </div>
              
              {/* Suggestions if any */}
              {selectedNote.suggestions && (
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500" />
                    Suggestions for Improvement
                  </h4>
                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-foreground">
                    {selectedNote.suggestions}
                  </div>
                </div>
              )}
              
              {/* Read Status */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2 text-sm">
                  {selectedNote.is_read_by_user ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-muted-foreground">Marked as read</span>
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 text-blue-500" />
                      <span className="text-muted-foreground">New note</span>
                    </>
                  )}
                </div>
                <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
